import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Grid } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import pointInPolygon from "point-in-polygon";
import { useTranslation } from "react-i18next";

import BasicLayout from "layouts/authentication/components/BasicLayout";
import { actions } from "store/reducers/qrCodeClockIn";
import Loading from "components/NewLoading";
import MDBox from "components/MDBox";
import StorageManager from "helpers/StorageManager";
import image1 from "assets/images/prichard/bg_1.webp";
import image2 from "assets/images/prichard/bg_2.webp";
import image3 from "assets/images/prichard/bg_3.webp";
import LogIn from "./LogIn";
import LoggedIn from "./LoggedIn";

const isLocationInArea = (location, area) =>
  pointInPolygon(
    [location.lat, location.lng],
    area.map((p) => [p.lat, p.lng])
  );

const images = [image1, image2, image3];

const gridSize = {
  xs: 12,
  sm: 10,
  md: 8,
  lg: 6,
  xl: 5,
};

const getBackgroundIndex = () => {
  const storageBackgroundIndex = parseInt(StorageManager.get("CLOCKIN_BG_INDEX"), 10);
  if (Number.isNaN(storageBackgroundIndex)) {
    return 0;
  }
  if (storageBackgroundIndex < 0 && storageBackgroundIndex > 2) {
    return 0;
  }
  return storageBackgroundIndex;
};

const useBackgroundIndex = () => {
  const [backgroundIndex] = useState(getBackgroundIndex());

  useEffect(() => {
    const newBackgroundIndex = backgroundIndex === 2 ? 1 : backgroundIndex + 1;
    StorageManager.set("CLOCKIN_BG_INDEX", newBackgroundIndex);
  }, [backgroundIndex]);

  return getBackgroundIndex();
};

function QrCodeClockIn() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [hasGeoLocation, setHasGeoLocation] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const backgroundIndex = useBackgroundIndex();

  const {
    isFetchingQrCode,
    fetchingQrCodeError,
    qrCode,
    hasValidatedLocation,
    isValidatingLocation,
    validatingLocationError,
    isValidLocation,
    isLoggedIn,
    isCheckingIfClockedIn,
    isClockingIn,
    isCheckingToken,
  } = useSelector((state) => state.qrCodeClockIn);

  const content = useMemo(() => {
    if (isLoggedIn) {
      return <LoggedIn />;
    }
    if (hasValidatedLocation && isValidLocation && !isCheckingToken) {
      return <LogIn />;
    }
    if (
      isFetchingQrCode ||
      isGettingLocation ||
      isValidatingLocation ||
      isCheckingIfClockedIn ||
      isClockingIn ||
      isCheckingToken
    ) {
      return (
        <div style={{ textAlign: "center" }}>
          <Loading />
        </div>
      );
    }
    if (qrCode) {
      if (!hasGeoLocation) {
        return (
          <Card>
            <MDBox py={2}>
              <div style={{ textAlign: "center" }}>{t("Geo location is required")}</div>
            </MDBox>

            <MDBox py={2} pl={4} pr={2}>
              <div style={{ textAlign: "center", marginBottom: 10 }}>
                {t("To enable geo location in")}:
              </div>
              <Grid container>
                <Grid item xs={12} md={6}>
                  <div>{t("IOS")}</div>
                  <ol style={{ fontSize: 16 }}>
                    <li>{t("Go to ‘Settings’")}</li>
                    <li>{t("Go to ‘Privacy’")}</li>
                    <li>{t("Go to ‘Location Services’")}</li>
                    <li>{t("Toggle the slider to the right to turn on/green.")}</li>
                  </ol>
                </Grid>

                <Grid item xs={12} md={6}>
                  <div>{t("Android")}</div>
                  <ol style={{ fontSize: 16 }}>
                    <li>{t("Go to ‘Settings’")}</li>
                    <li>{t("Go to ‘Location’")}</li>
                    <li>{t("Move the slider to the right for on/green")}</li>
                  </ol>
                </Grid>
              </Grid>
            </MDBox>

            <MDBox py={2} pl={4} pr={2}>
              <div style={{ textAlign: "center", marginBottom: 10 }}>
                {t("If you are in IOS and still can not use the location")}:
              </div>
              <div>
                <ol style={{ fontSize: 16 }}>
                  <li>{t("Go to ‘Settings’")}</li>
                  <li>{t("Go to ‘Privacy’")}</li>
                  <li>{t("Go to ‘Location Services’")}</li>
                  <li>
                    {t("Scroll down to find your browser, Tap the browser and select ‘Always’")}
                  </li>
                </ol>
              </div>
            </MDBox>
          </Card>
        );
      }
      if (validatingLocationError) {
        return (
          <Card>
            <MDBox py={2}>
              <div style={{ textAlign: "center" }}>{t("Your location is not valid")}</div>
            </MDBox>
          </Card>
        );
      }
    } else {
      return (
        <Card>
          <MDBox py={2}>
            <div style={{ textAlign: "center" }}>{t("QR code not found")}</div>
          </MDBox>
        </Card>
      );
    }
    return null;
  }, [
    qrCode,
    isFetchingQrCode,
    hasGeoLocation,
    isGettingLocation,
    isValidatingLocation,
    hasValidatedLocation,
    isValidLocation,
    isLoggedIn,
    isCheckingIfClockedIn,
    isClockingIn,
    validatingLocationError,
  ]);

  useEffect(() => {
    if (qrCode && !isFetchingQrCode && isValidLocation) {
      dispatch(actions.requestClockInCheckToken());
    }
  }, [qrCode, isFetchingQrCode, isValidLocation]);
  // this will most likely never happen but can never be too sure
  useEffect(() => {
    if (!id) {
      toast.error(t("Bad qr code"));
      navigate("/");
    }
  }, [id]);

  useEffect(() => {
    dispatch(actions.requestFetchQrCode(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (fetchingQrCodeError) {
      // toast.error(fetchingQrCodeError);
    }
  }, [fetchingQrCodeError]);

  useEffect(() => {
    if (qrCode) {
      if (qrCode.redirectTo) {
        window.location.replace(`${qrCode.redirectTo}/${qrCode.id}`);
      } else if ("geolocation" in navigator) {
        setIsGettingLocation(true);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setHasGeoLocation(true);
            const { latitude, longitude } = position.coords;
            setIsGettingLocation(false);
            const location = { lat: latitude, lng: longitude };
            if (isLocationInArea(location, qrCode.area)) {
              dispatch(actions.requestValidateLocation(location));
            } else {
              dispatch(actions.validateLocationFailed(t("Your location is not valid")));
            }
          },
          () => {
            setHasGeoLocation(false);
            setIsGettingLocation(false);
          }
        );
      } else {
        toast.error(t("Geolocation is required for you to use this feature"));
        /* geolocation IS NOT available, handle it */
      }
    }
  }, [dispatch, qrCode, setIsGettingLocation, setHasGeoLocation]);

  return (
    <BasicLayout image={images[backgroundIndex]} gridSize={gridSize}>
      <div>{content}</div>
    </BasicLayout>
  );
}

export default QrCodeClockIn;
